import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { api } from '../services/api';
import TimetableManager from './TimetableManager';

const ScolaritePage = () => {

  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState('discipline');
  // L'onglet « Emploi du temps » rendait `<TimetableManager />` sans annee :
  // `activeYear.id` levait une TypeError des qu'on enregistrait un creneau.
  const [anneeScolaireActive, setAnneeScolaireActive] = useState(null);
  const [disciplineType, setDisciplineType] = useState('absences');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);



  // États principaux pour la gestion des notes
const [allNotes, setAllNotes] = useState({
  '1': {}, // Notes du premier trimestre
  '2': {}, // Notes du deuxième trimestre
  '3': {}  // Notes du troisième trimestre
});

// État pour les moyennes de classe par trimestre
const [moyennesClasse, setMoyennesClasse] = useState({
  '1': {},
  '2': {},
  '3': {}
});


 const [selectedClasse, setSelectedClasse] = useState('');
 const [selectedTrimestre, setSelectedTrimestre] = useState('1');
 const [classes, setClasses] = useState([]);
 const [classeMatieres, setClasseMatieres] = useState([]);
 const [classeEleves, setClasseEleves] = useState([]);
 const [notes, setNotes] = useState({});
 

  // État pour les données disciplinaires
  const [editingSuiviId, setEditingSuiviId] = useState(null);
  const [disciplineSuivis, setDisciplineSuivis] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedEleve, setSelectedEleve] = useState('');
  const [eleves, setEleves] = useState([]);
  const [disciplineData, setDisciplineData] = useState({
    date: '',
    dureeValeur: '', // MODIFIÉ: Remplace l'ancien champ 'duree'
    dureeUnite: 'heures', // AJOUT: Nouvelle propriété pour l'unité de durée
    motif: '',
    type: disciplineType,
    annee_scolaire: '2023-2024'
  });

  // Les effets 
  useEffect(() => {
    loadEleves();
  }, []);
  
  useEffect(() => {
    if (selectedClasse) {
      loadClasseData();
    }
  }, [selectedClasse, selectedTrimestre]);

  // Effet pour charger les classes au chargement initial
  useEffect(() => {
    if (activeForm === 'notes') {
      loadClasses();
    }
  }, [activeForm]);

  // L'annee, avec ses dates, a l'ouverture de l'onglet emploi du temps.
  // `getActiveAvecDates` et non `getActive` : ce dernier rend un objet
  // fabrique depuis le localStorage, sans `date_debut` ni `date_fin`, et la
  // generation annuelle des creneaux en a besoin.
  useEffect(() => {
    if (activeForm !== 'emploi-du-temps' || anneeScolaireActive) return;
    (async () => {
      try {
        const reponse = await api.anneesScolaires.getActiveAvecDates();
        if (reponse.success && reponse.data) setAnneeScolaireActive(reponse.data);
      } catch (erreur) {
        console.error("Erreur chargement de l'annee scolaire:", erreur);
      }
    })();
  }, [activeForm, anneeScolaireActive]);

  const loadEleves = async () => {
    try {
      const data = await api.eleves.getAll();
      setEleves(data);
    } catch (error) {
      setError("Erreur lors du chargement des élèves");
    }
  };

  // AJOUT: Chargement des classes
  const loadClasses = async () => {
    try {
      const data = await api.classes.getAll();
      setClasses(data);
    } catch (error) {
      setError("Erreur lors du chargement des classes");
    }
  };
  

  const loadClasseData = async () => {
    setLoading(true);
    try {
      // Charger les données en parallèle avec le trimestre sélectionné
      const [elevesData, matieresData, notesData, moyennesData] = await Promise.all([
        api.classes.getEleves(selectedClasse),
        api.scolarite.getMatieres(selectedClasse),
        api.scolarite.getNotesByClasse(selectedClasse, selectedTrimestre),
        api.scolarite.getMoyennesClasse(selectedClasse, selectedTrimestre)
      ]);
  
      setClasseEleves(elevesData);
      setClasseMatieres(matieresData);
  
      // Formatage des notes pour le trimestre actuel
      const notesFormatted = {};
      notesData
        .filter(note => note.trimestre === selectedTrimestre)
        .forEach(note => {
          const key = `${note.eleve_id}-${note.matiere_id}`;
          notesFormatted[key] = {
            notes: note.notes || '',
            moyenne: parseFloat(note.moyenne) || 0,
            eleve_id: note.eleve_id,
            matiere_id: note.matiere_id,
            trimestre: selectedTrimestre,
            annee_scolaire: '2023-2024'
          };
        });
      setNotes(notesFormatted);
  
      // Formatage des moyennes de classe pour le trimestre actuel
      const moyennesFormatted = {};
      moyennesData
        .filter(moyenne => moyenne.trimestre === selectedTrimestre)
        .forEach(moyenne => {
          const moyenneValue = parseFloat(moyenne.moyenne_classe);
          if (!isNaN(moyenneValue)) {
            moyennesFormatted[moyenne.matiere_id] = moyenneValue;
          }
        });
      setMoyennesClasse(moyennesFormatted);
  
      setError(null);
    } catch (error) {
      setError("Erreur lors du chargement des données de la classe");
      console.error('Erreur détaillée:', error);
    } finally {
      setLoading(false);
    }
  };





  // AJOUT: Nouvelle fonction pour formater la durée avant l'envoi à l'API
    const formatDuree = (valeur, unite) => {
      if (!valeur) return null;
      
      const val = parseInt(valeur);
      let minutes = 0;
      
      switch(unite) {
        case 'minutes':
          minutes = val;
          break;
        case 'heures':
          minutes = val * 60;
          break;
        case 'jours':
          minutes = val * 60 * 24;
          break;
        case 'semaines':
          minutes = val * 60 * 24 * 7;
          break;
      }
      
      const heures = Math.floor(minutes / 60);
      const minutesRestantes = minutes % 60;
      
      return `${String(heures).padStart(2, '0')}:${String(minutesRestantes).padStart(2, '0')}`;
    };

  const loadDisciplineSuivis = async () => {
    try {
      const data = await api.discipline.getSuivisByEleve(selectedEleve);
      setDisciplineSuivis(data || []);
    } catch (error) {
      setError("Erreur lors du chargement des suivis disciplinaires");
    }
  };
  
  // Ajouter cet useEffect
  useEffect(() => {
    if (selectedEleve) {
      loadDisciplineSuivis();
    }
  }, [selectedEleve]);

  // État pour les notes
  const [notesData, setNotesData] = useState({
    trimestre1: [],
    trimestre2: [],
    trimestre3: []
  });

  // État pour les matières
  const [matieres, setMatieres] = useState([]);

  useEffect(() => {
    loadMatieres();
  }, []);

  const loadMatieres = async () => {
    try {
      const data = await api.scolarite.getMatieres();
      setMatieres(data);
      initializeNotesData(data);
    } catch (error) {
      setError("Erreur lors du chargement des matières");
    }
  };

  const initializeNotesData = (matieresList) => {
    const initialState = {
      trimestre1: [],
      trimestre2: [],
      trimestre3: []
    };

    ['trimestre1', 'trimestre2', 'trimestre3'].forEach(trimestre => {
      matieresList.forEach(matiere => {
        initialState[trimestre].push({
          matiere_id: matiere.id,
          matiere_nom: matiere.nom,
          coefficient: matiere.coefficient,
          notes: '',
          moyenne: 0
        });
      });
    });

    setNotesData(initialState);
  };

  const handleDisciplineChange = (e) => {
    const { name, value } = e.target;
    setDisciplineData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // MODIFICATION: Mise à jour de handleDisciplineSave pour utiliser le nouveau format de durée
const handleDisciplineSave = async () => {
  if (!selectedEleve) return;
  
  try {
    setLoading(true);
    
    const suiviData = {
      date_evenement: disciplineData.date,
      duree: formatDuree(disciplineData.dureeValeur, disciplineData.dureeUnite), // MODIFIÉ
      motif: disciplineData.motif,
      type: disciplineType,
      annee_scolaire: disciplineData.annee_scolaire
    };

    if (editingSuiviId) {
      await api.discipline.updateSuivi(selectedEleve, editingSuiviId, suiviData);
    } else {
      await api.discipline.createSuivi(selectedEleve, suiviData);
    }
    
    await loadDisciplineSuivis();
    
    // MODIFICATION: Mise à jour de la réinitialisation du formulaire
    setDisciplineData({
      date: '',
      dureeValeur: '',
      dureeUnite: 'heures',
      motif: '',
      type: disciplineType,
      annee_scolaire: '2023-2024'
    });
    setEditingSuiviId(null);
  } catch (error) {
    setError("Erreur lors de l'enregistrement");
    console.error('Erreur détaillée:', error);
  } finally {
    setLoading(false);
  }
};

  const handleEdit = (suivi) => {
    setEditingSuiviId(suivi.id); // Stocker l'ID du suivi à modifier
    setDisciplineData({
      date: suivi.date_evenement,
      duree: suivi.duree,
      motif: suivi.motif,
      type: suivi.type,
      annee_scolaire: suivi.annee_scolaire
    });
    setDisciplineType(suivi.type);
  };
  
  const handleDelete = async (suiviId) => {
    if (!selectedEleve) return;
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce suivi ?')) {
      try {
        setLoading(true);
        await api.discipline.deleteSuivi(selectedEleve, suiviId); // Appel correct avec les deux paramètres
        await loadDisciplineSuivis(); // Recharger la liste après suppression
      } catch (error) {
        setError("Erreur lors de la suppression du suivi");
        console.error('Erreur détaillée:', error);
      } finally {
        setLoading(false);
      }
    }
  };


   

  // AJOUT: Chargement des élèves d'une classe
  const loadClasseEleves = async (classeId) => {
    try {
      const data = await api.classes.getEleves(classeId);
      setClasseEleves(data);
    } catch (error) {
      setError("Erreur lors du chargement des élèves");
    }
  };

  // AJOUT: Chargement des matières d'une classe
  const loadClasseMatieres = async (classeId) => {
    try {
      // Récupération des matières spécifiques à la classe
      const data = await api.scolarite.getMatieresByClasse(classeId);
      
      // Tri des matières par ordre alphabétique
      const matieresTriees = data.sort((a, b) => a.nom.localeCompare(b.nom));
      
      setClasseMatieres(matieresTriees);
    } catch (error) {
      setError("Erreur lors du chargement des matières");
      console.error('Erreur détaillée:', error);
    }
  };

// AJOUT: Fonction de gestion du changement des notes
const handleNoteChange = (eleveId, matiereId, value) => {
  setNotes(prev => ({
    ...prev,
    [`${eleveId}-${matiereId}`]: {
      notes: value,
      moyenne: calculateMoyenne(value),
      eleve_id: eleveId,
      matiere_id: matiereId
    }
  }));
};

// MODIFICATION: Ajustement de la fonction calculateMoyenne existante
const calculateMoyenne = (notes) => {
  if (!notes || typeof notes !== 'string' || notes.trim() === '') return 0;
  
  const notesList = notes.split(',')
    .map(note => parseFloat(note.trim()))
    .filter(note => !isNaN(note) && note >= 0 && note <= 20);

  if (notesList.length === 0) return 0;

  const sum = notesList.reduce((acc, note) => acc + note, 0);
  return Math.round((sum / notesList.length) * 100) / 100;
};

// AJOUT: Calcul de la moyenne générale par élève
const calculateMoyenneGenerale = (eleveId) => {
  let totalPoints = 0;
  let totalCoef = 0;

  classeMatieres.forEach(matiere => {
    const noteInfo = notes[`${eleveId}-${matiere.id}`];
    if (noteInfo?.moyenne) {
      totalPoints += noteInfo.moyenne * matiere.coefficient;
      totalCoef += matiere.coefficient;
    }
  });

  if (totalCoef === 0) return '—';
  return (totalPoints / totalCoef).toFixed(2);
};

// AJOUT: Calcul de la moyenne de la classe pour une matière
const calculateMoyenneClasse = (matiereId) => {
  let total = 0;
  let count = 0;

  classeEleves.forEach(eleve => {
    const noteInfo = notes[`${eleve.id}-${matiereId}`];
    if (noteInfo?.moyenne) {
      total += noteInfo.moyenne;
      count++;
    }
  });

  if (count === 0) return '—';
  return (total / count).toFixed(2);
};


// AJOUT: Fonction de sauvegarde de toutes les notes
const handleSaveAllNotes = async () => {
  if (!selectedClasse) return;

  try {
    setLoading(true);
    setError(null);

    // Préparer les notes pour l'envoi
    const notesToSave = Object.values(notes)
      .filter(note => note.notes && note.notes.trim() !== '')
      .map(note => ({
        ...note,
        trimestre: selectedTrimestre,
        annee_scolaire: '2023-2024',
        coefficient: classeMatieres.find(m => m.id === note.matiere_id)?.coefficient || 1,
        date_evaluation: new Date().toISOString().split('T')[0]  // Date du jour
      }));

    if (notesToSave.length === 0) {
      setError("Aucune note à enregistrer");
      return;
    }

    // Sauvegarder les notes
    await api.scolarite.saveNotesBulk(notesToSave);

    // Sauvegarder les moyennes générales
    const moyennesGenerales = classeEleves
      .map(eleve => {
        const moyenne = calculateMoyenneGenerale(eleve.id);
        return {
          eleve_id: eleve.id,
          trimestre: selectedTrimestre,
          moyenne_generale: parseFloat(moyenne === '—' ? 0 : moyenne),
          annee_scolaire: '2023-2024'
        };
      })
      .filter(mg => mg.moyenne_generale > 0);

    if (moyennesGenerales.length > 0) {
      await api.scolarite.saveMoyennesGenerales(moyennesGenerales);
    }

    // Recharger les données
    await loadClasseData();
    
    setSuccessMessage("Notes enregistrées avec succès");
    setTimeout(() => setSuccessMessage(null), 3000);
  } catch (error) {
    setError("Erreur lors de l'enregistrement des notes");
    console.error('Erreur détaillée:', error);
  } finally {
    setLoading(false);
  }
};

 const handleDeleteNote = async (eleveId, matiereId) => {
  if (!window.confirm('Voulez-vous vraiment supprimer cette note ?')) return;
  
  try {
    setLoading(true);
    await api.scolarite.deleteNote(eleveId, matiereId, selectedTrimestre);
    setNotes(prev => {
      const newNotes = { ...prev };
      delete newNotes[`${eleveId}-${matiereId}`];
      return newNotes;
    });
    setSuccessMessage("Note supprimée avec succès");
  } catch (error) {
    setError("Erreur lors de la suppression de la note");
  } finally {
    setLoading(false);
  }
};

// AJOUT: Fonction de chargement des notes existantes
const loadExistingNotes = async (classeId) => {
  try {
    const data = await api.scolarite.getNotesByClasse(classeId);
    
    // Formater les notes pour notre état
    const notesObj = {};
    data.forEach(note => {
      notesObj[`${note.eleve_id}-${note.matiere_id}`] = {
        notes: note.notes,
        moyenne: note.moyenne,
        eleve_id: note.eleve_id,
        matiere_id: note.matiere_id
      };
    });

    setNotes(notesObj);
  } catch (error) {
    setError("Erreur lors du chargement des notes");
    console.error('Erreur détaillée:', error);
  }
};
  
return (
  <div className="min-h-screen bg-gray-50 p-8">
    <div className="max-w-7xl mx-auto">
      {loading && <div className="text-center py-4">Chargement...</div>}
      {error && <div className="text-red-500 text-center py-4">{error}</div>}

      {/* Header avec boutons */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate('/administration')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={20} />
          Retour
        </button>

        <button
          onClick={() => navigate('/administration/scolarite/configuration')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Settings size={20} />
          Configuration
        </button>
      </div>

      {/* Navigation */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveForm('discipline')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeForm === 'discipline' ? 'bg-blue-600 text-white' : 'bg-gray-200'
          }`}
        >
          Suivi Disciplinaire
        </button>
        <button
          onClick={() => setActiveForm('notes')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeForm === 'notes' ? 'bg-blue-600 text-white' : 'bg-gray-200'
          }`}
        >
          Notes et Moyennes
        </button>
        <button
          onClick={() => setActiveForm('emploi-du-temps')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeForm === 'emploi-du-temps' ? 'bg-blue-600 text-white' : 'bg-gray-200'
          }`}
        >
          Emploi du temps
        </button>
      </div>

      {/* Section Disciplinaire */}
    

      {activeForm === 'discipline' && (
  <div className="bg-white p-6 rounded-xl shadow-sm">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-semibold">Suivi Disciplinaire</h3>
    </div>

    {/* Sélecteur d'élève */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">Élève</label>
      <select
        value={selectedEleve}
        onChange={(e) => setSelectedEleve(e.target.value)}
        className="w-full rounded-lg border-gray-200 p-2.5"
      >
        <option value="">Sélectionner un élève</option>
        {eleves.map(eleve => (
          <option key={eleve.id} value={eleve.id}>
            {eleve.nom} {eleve.prenom} - {eleve.classe_nom}
          </option>
        ))}
      </select>
    </div>

    <div className="flex gap-4 mb-4 overflow-x-auto">
      {['absences', 'retards', 'sanctions', 'incidents'].map(type => (
        <button
          key={type}
          onClick={() => setDisciplineType(type)}
          disabled={!selectedEleve}
          className={`px-3 py-1 rounded-md capitalize transition-colors ${
            disciplineType === type ? 'bg-blue-100 text-blue-600' : 'bg-gray-50'
          } ${!selectedEleve ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {type}
        </button>
      ))}
    </div>

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Date</label>
        <input
          type="date"
          name="date"
          value={disciplineData.date}
          onChange={handleDisciplineChange}
          disabled={!selectedEleve || loading}
          className="w-full rounded-lg border-gray-200 p-2.5 disabled:bg-gray-100"
        />
      </div>

      {/* MODIFICATION: Remplacement du champ de durée existant */}
      <div>
        <label className="block text-sm font-medium mb-1">Durée</label>
        <div className="flex gap-2">
          <input
            type="number"
            name="dureeValeur"
            value={disciplineData.dureeValeur || ''}
            onChange={handleDisciplineChange}
            min="0"
            className="w-24 rounded-lg border-gray-200 p-2.5"
            placeholder="Durée"
            disabled={!selectedEleve || loading}
          />
          <select
            name="dureeUnite"
            value={disciplineData.dureeUnite}
            onChange={handleDisciplineChange}
            className="rounded-lg border-gray-200 p-2.5"
            disabled={!selectedEleve || loading}
          >
            <option value="minutes">Minutes</option>
            <option value="heures">Heures</option>
            <option value="jours">Jours</option>
            <option value="semaines">Semaines</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Motif</label>
        <textarea
          name="motif"
          value={disciplineData.motif}
          onChange={handleDisciplineChange}
          disabled={!selectedEleve || loading}
          className="w-full rounded-lg border-gray-200 p-2.5 disabled:bg-gray-100"
          rows="3"
        />
      </div>

      {selectedEleve && (
        <div className="flex justify-end gap-2 mt-4">
         <button
          onClick={() => {
            setSelectedEleve('');
            setDisciplineData({
              date: '',
              duree: '',
              motif: '',
              type: disciplineType,
              annee_scolaire: '2023-2024'
            });
            setEditingSuiviId(null); // Réinitialiser l'ID du suivi en cours de modification
          }}
          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          disabled={loading}
        >
          Annuler
        </button>

          <button
            onClick={handleDisciplineSave}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      )}

      {/* Section Liste des suivis disciplinaires */}
      {selectedEleve && (
        <div className="mt-8 border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold">Historique des suivis disciplinaires</h4>
            <div className="flex gap-4">
              <input
                type="text"
                placeholder="Rechercher par motif ou type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rounded-lg border-gray-200 p-2 w-64"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Durée</th>
                  <th className="p-2 text-left">Motif</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {disciplineSuivis
                  .filter(suivi => 
                    suivi.motif?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    suivi.type?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((suivi) => (
                    <tr key={suivi.id} className="border-b">
                      <td className="p-2">
                        {new Date(suivi.date_evenement).toLocaleDateString()}
                      </td>
                      <td className="p-2 capitalize">{suivi.type}</td>
                      <td className="p-2">{suivi.duree}</td>
                      <td className="p-2">{suivi.motif}</td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(suivi)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(suivi.id)} 
                            className="text-red-600 hover:text-red-800"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                {disciplineSuivis.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-gray-500">
                      Aucun suivi disciplinaire enregistré
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  </div>
)}


{/* Section Notes et Moyennes */}
{activeForm === 'notes' && (
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="mb-6 space-y-4">
              {/* Sélecteur de classe */}
              <div>
                <label className="block text-sm font-medium mb-1">Classe</label>
                <select
                  value={selectedClasse}
                  onChange={(e) => setSelectedClasse(e.target.value)}
                  className="w-full rounded-lg border-gray-200 p-2.5"
                >
                  <option value="">Sélectionner une classe</option>
                  {classes.map(classe => (
                    <option key={classe.id} value={classe.id}>{classe.nom}</option>
                  ))}
                </select>
              </div>

              {/* Sélecteur de trimestre */}
              <div>
                <label className="block text-sm font-medium mb-1">Trimestre</label>
                <select
                  value={selectedTrimestre}
                  onChange={(e) => setSelectedTrimestre(e.target.value)}
                  className="w-full rounded-lg border-gray-200 p-2.5"
                >
                  <option value="1">1er Trimestre</option>
                  <option value="2">2ème Trimestre</option>
                  <option value="3">3ème Trimestre</option>
                </select>
              </div>
            </div>

            {selectedClasse && (
              <>
                {/* Tableau des notes */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-2 text-left">Élève</th>
                      {classeMatieres.map(matiere => (
                        <th key={matiere.id} className="p-2">
                          <div>{matiere.nom}</div>
                          <div className="text-xs text-gray-500">
                            Coef. {matiere.coefficient}
                          </div>
                          {typeof moyennesClasse[matiere.id] === 'number' && (
                            <div className="text-xs text-blue-500">
                              Moyenne: {moyennesClasse[matiere.id].toFixed(2)}
                            </div>
                          )}
                        </th>
                      ))}
                      <th className="p-2">Moyenne générale</th>
                    </tr>
                  </thead>
                    <tbody>
                      {classeEleves.map(eleve => (
                        <tr key={eleve.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">
                          {eleve.nom} {eleve.prenom}
                        </td>
                        {classeMatieres.map(matiere => (
                          <td key={matiere.id} className="p-2">
                            <div className="relative group">
                              <input
                                type="text"
                                value={notes[`${eleve.id}-${matiere.id}`]?.notes || ''}
                                onChange={(e) => handleNoteChange(
                                  eleve.id,
                                  matiere.id,
                                  e.target.value
                                )}
                                placeholder="Ex: 15,12,14"
                                className="w-full rounded border-gray-200 p-1 text-center"
                              />
                              {notes[`${eleve.id}-${matiere.id}`]?.notes && (
                                <button
                                  onClick={() => handleDeleteNote(eleve.id, matiere.id)}
                                  className="absolute right-0 top-0 -mr-6 opacity-0 group-hover:opacity-100 
                                           text-red-600 hover:text-red-800 transition-opacity"
                                  title="Supprimer la note"
                                >
                                  ×
                                </button>
                              )}
                              {notes[`${eleve.id}-${matiere.id}`]?.moyenne && (
                                <div className="text-xs text-gray-500 mt-1 text-center">
                                  {typeof notes[`${eleve.id}-${matiere.id}`].moyenne === 'number' 
                                    ? notes[`${eleve.id}-${matiere.id}`].moyenne.toFixed(2)
                                    : '—'
                                  }
                                </div>
                              )}
                            </div>
                          </td>
                        ))}
                        <td className="p-2 font-medium text-center">
                          {calculateMoyenneGenerale(eleve.id)}
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Boutons d'action */}
                <div className="mt-6 flex justify-end space-x-2">
                  <button
                    onClick={handleSaveAllNotes}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg 
                             hover:bg-green-700 transition-colors disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Enregistrement...' : 'Enregistrer toutes les notes'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

      {/* Section Emploi du temps */}
      {activeForm === 'emploi-du-temps' && (
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Emploi du temps</h3>
          </div>
          <TimetableManager activeYear={anneeScolaireActive} />
        </div>
      )}
    </div>
  </div>
);
};

export default ScolaritePage;